import { Injectable, Injector, inject } from '@angular/core';
import { ShopTone, toShopTone } from '@digilize/shared/definitions/src';
import { Translation, TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';

import { TranslocoHttpLoader } from './transloco-loader';

/**
 * Which register the customer-facing copy is written in: formal ("u / uw") or informal
 * ("jij / je / jou"). The shop declares it (`Shop.tone`, PAUL-3452) and the frontend renders it —
 * the backend switches no wording of its own.
 *
 * **The tone has to be known before the first translation file is requested**, and the shop only
 * arrives over HTTP. So the value is cached in localStorage and read back *synchronously* here, the
 * way `BrandingService` caches a shop's colours: `TranslocoHttpLoader` reads {@link current} on its
 * very first call and picks the right files with no bootstrap delay. When the live shop lands,
 * {@link setTone} corrects the already-loaded translations if the cache guessed wrong.
 *
 * Formal is the default and the fallback for every unknown value, because every shop is formal until
 * an admin opts out — a missing informal string degrades to formal, never the other way round.
 *
 * The cache key is per origin, and the portal (`/user/`), the webshop (`/`) and the dashboard
 * (`/admin/`) share one in production. That is right for the two customer-facing apps and harmless
 * for the dashboard today, because only `pauly-auth` ships overlay files. Before adding an overlay to
 * the **shared** assets in `libs/shared/assets/src/lib/i18n`, note that it would reach the staff apps
 * too — tone is a customer-facing setting, and the cash register and dashboard are out of its scope.
 */
@Injectable({ providedIn: 'root' })
export class ShopToneService {
  static readonly CACHE_KEY = 'pauly_shop_tone';

  /* Lazily resolved: TranslocoHttpLoader injects this service, so taking either of these in the
     constructor would close a DI cycle. */
  private injector = inject(Injector);

  private readonly tone$ = new BehaviorSubject<ShopTone>(this.readCache());

  readonly toneChanges$ = this.tone$.asObservable();

  /** Read synchronously by `TranslocoHttpLoader` on every `getTranslation` call. */
  get current(): ShopTone {
    return this.tone$.value;
  }

  get isInformal(): boolean {
    return this.tone$.value === ShopTone.Informal;
  }

  /**
   * Called once the real shop is known. A no-op when it matches what the loader already used;
   * otherwise it persists the new tone and re-reads every translation that is already in memory,
   * which is symmetric — it repairs a formal→informal miss and an informal→formal one alike.
   */
  setTone(tone: ShopTone): void {
    const next = toShopTone(tone);

    if (next === this.tone$.value) {
      return;
    }

    this.tone$.next(next);
    this.writeCache(next);
    this.reloadLoadedTranslations();
  }

  /** Re-runs the loader for every lang/scope Transloco has already resolved, now that the tone changed. */
  private reloadLoadedTranslations(): void {
    const transloco = this.injector.get(TranslocoService);
    const loader = this.injector.get(TranslocoHttpLoader);
    const loaded = Array.from(transloco.getTranslation().keys());

    if (!loaded.length) {
      return;
    }

    const reloads = loaded.map((lang) => loader.getTranslation(lang) as Observable<Translation>);

    forkJoin(reloads).subscribe((translations) => {
      translations.forEach((translation, index) =>
        /* merge, so a lang that has no overlay file keeps whatever else was set on it. */
        transloco.setTranslation(translation, loaded[index], { merge: true })
      );
    });
  }

  private readCache(): ShopTone {
    try {
      return localStorage.getItem(ShopToneService.CACHE_KEY) === String(ShopTone.Informal)
        ? ShopTone.Informal
        : ShopTone.Formal;
    } catch {
      return ShopTone.Formal;
    }
  }

  private writeCache(tone: ShopTone): void {
    try {
      localStorage.setItem(ShopToneService.CACHE_KEY, String(tone));
    } catch {
      /* A visitor with storage blocked simply pays the one-request correction on every load. */
    }
  }
}
